// Dashboard Service
// Este servicio maneja las estadísticas del dashboard del profesor

import ApiService from './ApiService';

const APP_TIME_ZONE = 'America/Argentina/Buenos_Aires';

const appDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const toDateKeyFromParts = ({ year, month, day }) => (
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
);

const getAppDateParts = (date) => {
  const parts = appDateFormatter.formatToParts(date).reduce((acc, part) => {
    if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
      acc[part.type] = Number(part.value);
    }
    return acc;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day
  };
};

const toDateKey = (date) => {
  return toDateKeyFromParts(getAppDateParts(date));
};

const normalizeDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : toDateKey(value);
  }

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return toDateKey(date);
};

const buildLastSevenDays = (referenceDate = new Date()) => {
  const todayParts = getAppDateParts(referenceDate);
  const today = new Date(
    Date.UTC(todayParts.year, todayParts.month - 1, todayParts.day)
  );

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() - 6 + index);
    return {
      key: toDateKeyFromParts({
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate()
      })
    };
  });
};

const formatDateLabel = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return dateKey;
  return `${match[3]}/${match[2]}`;
};

class DashboardService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7156/api';
  }

  // Obtener estadísticas del dashboard del profesor
  async getEstadisticas() {
    try {
      const results = await Promise.allSettled([
        this.getEstadisticasThreadBased(),
        this.getEstadisticasGptClasicos()
      ]);

      const exitosas = results
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);

      if (exitosas.length === 0) {
        const firstError = results.find((result) => result.status === 'rejected')?.reason;
        throw firstError || new Error('No pudimos obtener las estadísticas del dashboard');
      }

      return this.combinarEstadisticas(exitosas);
    } catch (error) {
      console.error('❌ Error al obtener estadísticas del dashboard:', error);
      throw error;
    }
  }

  async getEstadisticasThreadBased() {
    try {
      ApiService.debugTokenStatus();

      return await ApiService.get('/DashboardProfesor/estadisticas');
    } catch (error) {
      console.error('❌ Error al obtener estadísticas de asistentes con threads:', error);

      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw error;
      } else if (error.message.includes('403')) {
        throw new Error('Acceso denegado. Contacta al administrador del sistema');
      } else if (error.message.includes('404')) {
        throw new Error('Endpoint de estadísticas no encontrado');
      } else if (error.message.includes('500')) {
        throw new Error('Error del servidor al obtener estadísticas');
      } else if (error.message.includes('Network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }

      throw error;
    }
  }

  async getEstadisticasGptClasicos() {
    try {
      
      // Debug del estado del token antes de hacer la petición
      ApiService.debugTokenStatus();
      
      const response = await ApiService.get('/DashboardProfesor/gpt-clasicos/estadisticas');
      
      // Log de la respuesta recibida
      
      return response;
      
    } catch (error) {
      console.error('❌ Error al obtener estadísticas GPT clásicas del dashboard:', error);
      
      // Manejo de errores específicos
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // No hacer nada aquí ya que el ApiService ya maneja la redirección automática
        throw error; // Re-lanzar el error para que el componente pueda manejarlo si es necesario
      } else if (error.message.includes('403')) {
        throw new Error('Acceso denegado. Contacta al administrador del sistema');
      } else if (error.message.includes('404')) {
        throw new Error('Endpoint de estadísticas GPT clásicas no encontrado');
      } else if (error.message.includes('500')) {
        throw new Error('Error del servidor al obtener estadísticas GPT clásicas');
      } else if (error.message.includes('Network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }
      
      throw error;
    }
  }

  combinarEstadisticas(estadisticasList) {
    const acumulado = {
      totalAsistentesAsignados: 0,
      totalConversacionesCreadas: 0,
      conversacionesActivasHoy: 0,
      conversacionesActivasEstaSemana: 0,
      conversacionesActivasEsteMes: 0,
      usosPorAsistente: [],
      actividadPorDia: [],
      actividadPorAsistenteDia: [],
      asistenteMasUsado: 'Ninguno',
      fechaPrimeraConversacion: null,
      fechaUltimaConversacion: null
    };

    const usosPorKey = new Map();
    const actividadPorFecha = new Map();
    const actividadPorAsistenteFecha = new Map();

    const minDate = (current, candidate) => {
      if (!candidate) return current;
      if (!current) return candidate;
      return new Date(candidate) < new Date(current) ? candidate : current;
    };

    const maxDate = (current, candidate) => {
      if (!candidate) return current;
      if (!current) return candidate;
      return new Date(candidate) > new Date(current) ? candidate : current;
    };

    estadisticasList.forEach((estadisticas) => {
      acumulado.totalAsistentesAsignados += Number(estadisticas.totalAsistentesAsignados || 0);
      acumulado.totalConversacionesCreadas += Number(estadisticas.totalConversacionesCreadas ?? estadisticas.totalThreadsCreados ?? 0);
      acumulado.conversacionesActivasHoy += Number(estadisticas.conversacionesActivasHoy ?? estadisticas.threadsActivosHoy ?? 0);
      acumulado.conversacionesActivasEstaSemana += Number(estadisticas.conversacionesActivasEstaSemana ?? estadisticas.threadsActivosEstaSemana ?? 0);
      acumulado.conversacionesActivasEsteMes += Number(estadisticas.conversacionesActivasEsteMes ?? estadisticas.threadsActivosEsteMes ?? 0);

      acumulado.fechaPrimeraConversacion = minDate(
        acumulado.fechaPrimeraConversacion,
        estadisticas.fechaPrimeraConversacion ?? estadisticas.fechaPrimerThread
      );
      acumulado.fechaUltimaConversacion = maxDate(
        acumulado.fechaUltimaConversacion,
        estadisticas.fechaUltimaConversacion ?? estadisticas.fechaUltimoThread
      );

      (estadisticas.usosPorAsistente || []).forEach((uso) => {
        const key = uso.idOpenAIAssistant || uso.nombreAsistente || 'sin-asistente';
        const current = usosPorKey.get(key) || {
          idOpenAIAssistant: uso.idOpenAIAssistant,
          nombreAsistente: uso.nombreAsistente || 'Sin nombre',
          totalConversaciones: 0,
          ultimoUso: null
        };

        current.totalConversaciones += Number(uso.totalConversaciones ?? uso.totalThreads ?? uso.totalUsos ?? 0);
        current.ultimoUso = maxDate(current.ultimoUso, uso.ultimoUso);
        usosPorKey.set(key, current);
      });

      (estadisticas.actividadPorDia || []).forEach((dia) => {
        const key = normalizeDateKey(dia.fecha);
        if (!key) return;

        const current = actividadPorFecha.get(key) || {
          fecha: key,
          conversacionesCreadas: 0
        };
        current.conversacionesCreadas += Number(dia.conversacionesCreadas ?? dia.threadsCreados ?? 0);
        actividadPorFecha.set(key, current);
      });

      (estadisticas.actividadPorAsistenteDia || []).forEach((dia) => {
        const fecha = normalizeDateKey(dia.fecha);
        const idOpenAIAssistant = dia.idOpenAIAssistant || dia.IdOpenAIAssistant || dia.idAsistente || dia.IdAsistente;
        if (!fecha || !idOpenAIAssistant) return;

        const key = `${idOpenAIAssistant}::${fecha}`;
        const current = actividadPorAsistenteFecha.get(key) || {
          fecha,
          idOpenAIAssistant,
          nombreAsistente: dia.nombreAsistente || dia.NombreAsistente || 'Sin nombre',
          mensajesCreados: 0
        };
        current.mensajesCreados += Number(
          dia.mensajesCreados ??
          dia.MensajesCreados ??
          dia.totalMensajes ??
          dia.TotalMensajes ??
          dia.conversacionesCreadas ??
          dia.ConversacionesCreadas ??
          dia.threadsCreados ??
          dia.ThreadsCreados ??
          0
        );
        actividadPorAsistenteFecha.set(key, current);
      });
    });

    acumulado.usosPorAsistente = Array.from(usosPorKey.values())
      .sort((a, b) => b.totalConversaciones - a.totalConversaciones);
    acumulado.actividadPorDia = buildLastSevenDays().map(({ key }) => (
      actividadPorFecha.get(key) || {
        fecha: key,
        conversacionesCreadas: 0
      }
    ));
    acumulado.actividadPorAsistenteDia = Array.from(actividadPorAsistenteFecha.values())
      .sort((a, b) => {
        if (a.nombreAsistente !== b.nombreAsistente) {
          return a.nombreAsistente.localeCompare(b.nombreAsistente);
        }
        return a.fecha.localeCompare(b.fecha);
      });
    acumulado.asistenteMasUsado = acumulado.usosPorAsistente[0]?.nombreAsistente || 'Ninguno';

    return acumulado;
  }

  // Formatear datos para gráficos
  formatearDatosParaGraficos(estadisticas) {
    try {
      // Datos para el gráfico de actividad de hoy y los 6 días anteriores
      const actividadPorDia = estadisticas.actividadPorDia?.length > 0
        ? estadisticas.actividadPorDia
        : buildLastSevenDays().map(({ key }) => ({ fecha: key, conversacionesCreadas: 0 }));

      const actividadSemanal = actividadPorDia.map(dia => {
        const fechaCompleta = normalizeDateKey(dia.fecha);
        return {
          fecha: fechaCompleta ? formatDateLabel(fechaCompleta) : '',
          conversaciones: dia.conversacionesCreadas ?? dia.threadsCreados ?? 0,
          fechaCompleta: fechaCompleta || dia.fecha
        };
      });

      // Datos para gráfico de uso por asistente (si hay datos)
      const usosPorAsistente = estadisticas.usosPorAsistente?.map(uso => ({
        nombre: uso.nombreAsistente || 'Sin nombre',
        usos: uso.totalUsos ?? uso.totalConversaciones ?? uso.totalThreads ?? 0,
        porcentaje: uso.porcentajeUso || 0
      })) || [];

      const diasPorKey = new Map(
        actividadSemanal.map((dia) => [dia.fechaCompleta, dia])
      );

      const actividadPorAsistenteRaw = estadisticas.actividadPorAsistenteDia || [];
      const seriesPorAsistente = new Map();

      actividadPorAsistenteRaw.forEach((dia) => {
        const fechaCompleta = normalizeDateKey(dia.fecha);
        const idOpenAIAssistant = dia.idOpenAIAssistant || dia.IdOpenAIAssistant || dia.idAsistente || dia.IdAsistente || 'sin-asistente';
        if (!fechaCompleta || !diasPorKey.has(fechaCompleta)) return;

        const current = seriesPorAsistente.get(idOpenAIAssistant) || {
          idOpenAIAssistant,
          nombre: dia.nombreAsistente || dia.NombreAsistente || 'Sin nombre',
          puntosPorFecha: new Map()
        };

        current.puntosPorFecha.set(fechaCompleta, Number(
          dia.mensajesCreados ??
          dia.MensajesCreados ??
          dia.totalMensajes ??
          dia.TotalMensajes ??
          dia.conversacionesCreadas ??
          dia.ConversacionesCreadas ??
          0
        ));
        seriesPorAsistente.set(idOpenAIAssistant, current);
      });

      let actividadPorAsistente = Array.from(seriesPorAsistente.values())
        .map((serie) => ({
          idOpenAIAssistant: serie.idOpenAIAssistant,
          nombre: serie.nombre,
          totalMensajes: actividadSemanal.reduce((sum, dia) => (
            sum + (serie.puntosPorFecha.get(dia.fechaCompleta) || 0)
          ), 0),
          puntos: actividadSemanal.map((dia) => ({
            fecha: dia.fecha,
            fechaCompleta: dia.fechaCompleta,
            mensajes: serie.puntosPorFecha.get(dia.fechaCompleta) || 0
          }))
        }))
        .filter((serie) => serie.totalMensajes > 0);

      if (actividadPorAsistente.length === 0 && actividadSemanal.some((dia) => dia.conversaciones > 0)) {
        actividadPorAsistente = [{
          idOpenAIAssistant: 'total',
          nombre: 'Total',
          totalMensajes: actividadSemanal.reduce((sum, dia) => sum + dia.conversaciones, 0),
          puntos: actividadSemanal.map((dia) => ({
            fecha: dia.fecha,
            fechaCompleta: dia.fechaCompleta,
            mensajes: dia.conversaciones
          }))
        }];
      }

      // Resumen de estadísticas principales
      const resumenPrincipal = {
        totalAsistentes: estadisticas.totalAsistentesAsignados || 0,
        totalConversaciones: (estadisticas.totalConversacionesCreadas ?? estadisticas.totalThreadsCreados) || 0,
        conversacionesHoy: (estadisticas.conversacionesActivasHoy ?? estadisticas.threadsActivosHoy) || 0,
        conversacionesSemana: (estadisticas.conversacionesActivasEstaSemana ?? estadisticas.threadsActivosEstaSemana) || 0,
        conversacionesMes: (estadisticas.conversacionesActivasEsteMes ?? estadisticas.threadsActivosEsteMes) || 0,
        asistenteMasUsado: estadisticas.asistenteMasUsado || 'Ninguno'
      };


      return {
        actividadSemanal,
        actividadPorAsistente,
        usosPorAsistente,
        resumenPrincipal,
        fechaPrimeraConversacion: estadisticas.fechaPrimeraConversacion ?? estadisticas.fechaPrimerThread,
        fechaUltimaConversacion: estadisticas.fechaUltimaConversacion ?? estadisticas.fechaUltimoThread
      };

    } catch (error) {
      console.error('❌ Error al formatear datos para gráficos:', error);
      
      // Retornar datos por defecto en caso de error
      return {
        actividadSemanal: [],
        usosPorAsistente: [],
        resumenPrincipal: {
          totalAsistentes: 0,
          totalConversaciones: 0,
          conversacionesHoy: 0,
          conversacionesSemana: 0,
          conversacionesMes: 0,
          asistenteMasUsado: 'Ninguno'
        },
        actividadPorAsistente: [],
        fechaPrimeraConversacion: null,
        fechaUltimaConversacion: null
      };
    }
  }

  // Calcular tendencias y métricas adicionales
  calcularTendencias(datosFormateados) {
    try {
      const { actividadSemanal, resumenPrincipal } = datosFormateados;
      
      // Calcular promedio diario de la semana
      const totalConversacionesSemana = actividadSemanal.reduce((sum, dia) => sum + dia.conversaciones, 0);
      const promedioDiario = actividadSemanal.length > 0 ? 
        Math.round((totalConversacionesSemana / actividadSemanal.length) * 100) / 100 : 0;

      // Encontrar el día más activo
      const diaMasActivo = actividadSemanal.reduce((max, dia) => 
        dia.conversaciones > max.conversaciones ? dia : max, 
        { fecha: 'Ninguno', conversaciones: 0 }
      );

      // Calcular tasa de crecimiento (conversaciones este mes vs semana)
      const tasaCrecimiento = resumenPrincipal.conversacionesSemana > 0 ? 
        Math.round(((resumenPrincipal.conversacionesMes - resumenPrincipal.conversacionesSemana) / 
        resumenPrincipal.conversacionesSemana) * 100) : 0;


      return {
        promedioDiario,
        diaMasActivo,
        tasaCrecimiento,
        totalConversacionesSemana
      };

    } catch (error) {
      console.error('❌ Error al calcular tendencias:', error);
      return {
        promedioDiario: 0,
        diaMasActivo: { fecha: 'Ninguno', conversaciones: 0 },
        tasaCrecimiento: 0,
        totalConversacionesSemana: 0
      };
    }
  }
}

// Crear instancia singleton
const dashboardService = new DashboardService();

export default dashboardService;
